import { ExternalLink, Phone, CheckCircle2, AlertCircle } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';

interface CallEndedProps {
  /** The interaction id when the call connected and recorded; null if it
   *  errored before a token / interaction was minted. */
  interactionId: string | null;
  /** Caller-provided handler to start a new call. The component itself
   *  doesn't manage UI phase state — that lives in whoever mounts it. */
  onStartNew: () => void;
  /**
   * Optional inline-detail handler. When provided, the "Open post-call
   * view" button calls this instead of navigating to
   * `/activity/$interactionId` — so a parent that already mounts the
   * `InteractionDetailSheet` can swap the sheet contents inline rather
   * than tearing the user out of their current flow. The
   * `/activity/$interactionId` route is being deprecated; passing this
   * is the preferred path for new call sites.
   */
  onViewDetail?: (interactionId: string) => void;
}

/**
 * Post-call success screen. Shown when the LiveKit room disconnects
 * cleanly — surfaces the post-call view link and a "start another"
 * button. Shared by the task-overview drawer and the workflow builder's
 * test sheet so both render the same screen.
 */
export function CallEnded({
  interactionId,
  onStartNew,
  onViewDetail,
}: CallEndedProps): React.ReactElement {
  if (!interactionId) {
    return (
      <div className="flex flex-col items-center p-8">
        <div className="w-full max-w-md space-y-5 text-center">
          <div className="flex justify-center">
            <div className="rounded-full bg-warning/10 p-3 text-warning">
              <AlertCircle className="h-6 w-6" />
            </div>
          </div>
          <div>
            <h2 className="text-lg font-semibold">Call failed to start</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              The session ended before it connected. Try again.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={onStartNew} className="gap-2">
            <Phone className="h-3.5 w-3.5" />
            Start a new call
          </Button>
        </div>
      </div>
    );
  }

  // Render at the TOP of the column — not vertically centered. The
  // parent column scrolls, so a centered block lands below the fold on
  // tall viewports. Keep the action (the post-call link) immediately
  // visible.
  return (
    <div className="flex flex-col items-center p-8">
      <div className="w-full max-w-md space-y-5 text-center">
        <div className="flex justify-center">
          <div className="rounded-full bg-success/10 p-3 text-success">
            <CheckCircle2 className="h-6 w-6" />
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Call ended</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            The full transcript, tool calls, and call summary are available in the post-call view.
          </p>
        </div>
        <div className="flex flex-col items-center gap-2">
          {onViewDetail ? (
            <Button size="lg" className="w-full gap-2" onClick={() => onViewDetail(interactionId)}>
              <ExternalLink className="h-4 w-4" />
              Open post-call view
            </Button>
          ) : (
            <Button asChild size="lg" className="w-full gap-2">
              <Link to="/activity/$interactionId" params={{ interactionId }}>
                <ExternalLink className="h-4 w-4" />
                Open post-call view
              </Link>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onStartNew} className="gap-2">
            <Phone className="h-3.5 w-3.5" />
            Start another call
          </Button>
        </div>
      </div>
    </div>
  );
}
